import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, TIMEZONES, type CancelPolicy } from '@rydres/shared';
import { Badge, Button, Card, Confirm, Field, InfoTip, Input, Notice, QrCode, TextArea, ThemePicker, fmtPhone, toE164 } from '@rydres/ui';
import { billingApi, driverApi } from '../lib/api.js';
import { MISSING_LABELS, ProfileLoading, useProfile } from '../lib/profile.js';
import { useSession } from '../lib/session.js';
import type { BillingStatus, Profile, StripeStatus } from '../lib/types.js';
import { PhotoField } from './PhotoField.js';

const ITEMS = [
  { to: '/settings/profile', label: 'Business and page' },
  { to: '/settings/vehicle', label: 'Vehicle' },
  { to: '/settings/rules', label: 'Booking rules' },
  { to: '/settings/notifications', label: 'Notifications' },
  { to: '/settings/payments', label: 'Card payments' },
  { to: '/settings/billing', label: 'Subscription' },
  { to: '/reminders', label: 'Renewals' },
  { to: '/settings/share', label: 'Share your page' },
  { to: '/calculator', label: 'Price calculator' },
];

export function Settings() {
  const { me, signOut } = useSession();
  const { profile } = useProfile();
  // The setup steps the page still needs show here, on the rows that fix them, rather than on Today.
  const missing = new Set(profile?.check.missing ?? []);
  const todo = (to: string) => [...missing, ...(profile?.check.suggested ?? [])].some((m) => MISSING_LABELS[m]?.to === to);
  const rowsToDo = ITEMS.some((i) => todo(i.to));
  // The bottom bar has no room for Admin, so admins find it here.
  const items = me?.role === 'admin' ? [...ITEMS, { to: '/admin', label: 'Admin' }] : ITEMS;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Settings</h1>
      {profile && !profile.check.live && (
        <Notice tone="warn">
          Riders can't book yet.{' '}
          {rowsToDo && 'Finish the items marked to do'}
          {rowsToDo && missing.has('services') && ', then '}
          {missing.has('services') && <Link className="font-bold underline" to="/services?new=1">{rowsToDo ? 'add a service' : 'Add a service'}</Link>}.
        </Notice>
      )}
      <div className="space-y-2">
        {items.map((i) => (
          <Link key={i.to} to={i.to} className="flex items-center justify-between rounded-2xl border border-line bg-card p-4">
            <div className="flex items-center gap-2 font-bold">{i.label}{todo(i.to) && <Badge value="to do" tone="warn" />}</div>
            <span className="text-muted">›</span>
          </Link>
        ))}
      </div>
      <Card title="Appearance"><ThemePicker /></Card>
      <Card title="Account">
        <p className="text-sm text-muted">Signed in as {fmtPhone(me?.phone)}{profile?.handle ? ` · ${BRAND.publicOrigin.replace(/^https?:\/\//, '')}/${profile.handle}` : ''}</p>
        <Button variant="ghost" className="mt-3" onClick={() => void signOut()}>Sign out</Button>
      </Card>
      <DeleteAccount onDeleted={() => void signOut()} />
      <p className="text-center text-xs text-muted">{BRAND.name} provides software only; you provide the transportation under your own TCP authority.</p>
    </div>
  );
}

function useForm<T extends Record<string, unknown>>(initial: T) {
  const [f, setF] = useState<T>(initial);
  const set = <K extends keyof T>(k: K, v: T[K]) => setF((x) => ({ ...x, [k]: v }));
  return { f, set, setF };
}

function SaveBar({ busy, error, saved }: { busy: boolean; error: string | null; saved: boolean }) {
  return (
    <div className="space-y-2">
      {error && <Notice tone="danger">{error}</Notice>}
      {saved && <Notice tone="ok">Saved.</Notice>}
      <Button type="submit" disabled={busy} className="w-full">{busy ? 'Saving…' : 'Save'}</Button>
    </div>
  );
}

function useSave() {
  const { save } = useProfile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const run = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await save(patch);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  return { run, busy, error, saved };
}

function Back({ title, info }: { title: string; info?: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-3">
      <button onClick={() => navigate('/settings')} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
      <h1 className="text-xl font-black">{title}</h1>
      {info && <InfoTip label={title}>{info}</InfoTip>}
    </div>
  );
}

export function ProfileSettings() {
  const { profile } = useProfile();
  if (!profile) return <ProfileLoading />;
  return <ProfileForm p={profile} />;
}

function ProfileForm({ p }: { p: Profile }) {
  const { f, set } = useForm({
    handle: p.handle ?? '',
    displayName: p.displayName ?? '',
    legalName: p.legalName ?? '',
    tcpNumber: p.tcpNumber ?? '',
    phonePublic: p.phonePublic ?? p.phone ?? '',
    email: p.email ?? '',
    bio: p.bio ?? '',
    serviceAreaText: p.serviceAreaText ?? '',
    timezone: p.timezone,
  });
  const { run, busy, error, saved } = useSave();
  const [handleState, setHandleState] = useState<string | null>(null);
  useEffect(() => {
    if (!f.handle || f.handle === p.handle) return setHandleState(null);
    const t = setTimeout(() => {
      driverApi.handleAvailable(f.handle).then((r) => setHandleState(r.available ? `rydres.com/${r.handle} is free` : r.reason ?? 'taken'));
    }, 400);
    return () => clearTimeout(t);
  }, [f.handle, p.handle]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const phone = f.phonePublic ? toE164(f.phonePublic) : null;
    void run({
      ...(f.handle ? { handle: f.handle } : {}),
      displayName: f.displayName || undefined,
      legalName: f.legalName || null,
      tcpNumber: f.tcpNumber || null,
      phonePublic: phone,
      email: f.email || null,
      bio: f.bio || null,
      serviceAreaText: f.serviceAreaText || null,
      timezone: f.timezone,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Back title="Business and page" />
      <PhotoField kind="driver" url={p.photoUrl} label="Your photo" hint="Riders see it on your page and when you share your link." />
      <Field label="Page name" note={handleState ?? `Your link will be rydres.com/${f.handle || 'yourname'}`}>
        <Input value={f.handle} onChange={(e) => set('handle', e.target.value.toLowerCase())} placeholder="yourname" autoCapitalize="none" />
      </Field>
      <Field label="Your name (as riders see it)"><Input required value={f.displayName} onChange={(e) => set('displayName', e.target.value)} /></Field>
      <Field label="Business legal name" hint="Exactly as on your CPUC permit; printed on every waybill"><Input value={f.legalName} onChange={(e) => set('legalName', e.target.value)} /></Field>
      <Field label="TCP number" hint="e.g. TCP 12345-P"><Input value={f.tcpNumber} onChange={(e) => set('tcpNumber', e.target.value)} /></Field>
      <Field label="Phone riders can call"><Input inputMode="tel" value={f.phonePublic} onChange={(e) => set('phonePublic', e.target.value)} /></Field>
      <Field label="Email (optional)"><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
      <Field label="Service area" hint="Shown on your page"><Input value={f.serviceAreaText} onChange={(e) => set('serviceAreaText', e.target.value)} placeholder="Orange County, LAX, SNA" /></Field>
      <Field label="About you (optional)"><TextArea value={f.bio} onChange={(e) => set('bio', e.target.value)} placeholder="15 years of airport and corporate service. Black Escalade, water and chargers on board." /></Field>
      <Field label="Time zone">
        <select value={f.timezone} onChange={(e) => set('timezone', e.target.value)} className="w-full rounded-xl bg-card2 px-4 py-3 text-base outline-none">
          {TIMEZONES.map((z) => <option key={z}>{z}</option>)}
        </select>
      </Field>
      <SaveBar busy={busy} error={error} saved={saved} />
    </form>
  );
}

export function VehicleSettings() {
  const { profile } = useProfile();
  if (!profile) return <ProfileLoading />;
  return <VehicleForm p={profile} />;
}

function VehicleForm({ p }: { p: Profile }) {
  const { f, set } = useForm({
    vehicleYear: p.vehicleYear ? String(p.vehicleYear) : '',
    vehicleMake: p.vehicleMake ?? '',
    vehicleModel: p.vehicleModel ?? '',
    vehicleColor: p.vehicleColor ?? '',
    vehiclePlate: p.vehiclePlate ?? '',
    vehiclePlateState: p.vehiclePlateState ?? 'CA',
    vehicleSeats: p.vehicleSeats ? String(p.vehicleSeats) : '',
  });
  const { run, busy, error, saved } = useSave();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run({
      vehicleYear: f.vehicleYear ? Number(f.vehicleYear) : null,
      vehicleMake: f.vehicleMake || null,
      vehicleModel: f.vehicleModel || null,
      vehicleColor: f.vehicleColor || null,
      vehiclePlate: f.vehiclePlate.toUpperCase() || null,
      vehiclePlateState: f.vehiclePlateState || null,
      vehicleSeats: f.vehicleSeats ? Number(f.vehicleSeats) : null,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Back title="Vehicle" info="One car for now. These details go on every waybill." />
      <PhotoField kind="vehicle" url={p.vehiclePhotoUrl} label="Car photo" hint="Shown on your page so riders know the car when it arrives." />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Year"><Input inputMode="numeric" value={f.vehicleYear} onChange={(e) => set('vehicleYear', e.target.value)} /></Field>
        <Field label="Color"><Input value={f.vehicleColor} onChange={(e) => set('vehicleColor', e.target.value)} /></Field>
        <Field label="Make"><Input value={f.vehicleMake} onChange={(e) => set('vehicleMake', e.target.value)} placeholder="Cadillac" /></Field>
        <Field label="Model"><Input value={f.vehicleModel} onChange={(e) => set('vehicleModel', e.target.value)} placeholder="Escalade" /></Field>
        <Field label="Plate"><Input value={f.vehiclePlate} onChange={(e) => set('vehiclePlate', e.target.value)} autoCapitalize="characters" /></Field>
        <Field label="Plate state"><Input value={f.vehiclePlateState} maxLength={2} onChange={(e) => set('vehiclePlateState', e.target.value.toUpperCase())} /></Field>
        <Field label="Passenger seats"><Input inputMode="numeric" value={f.vehicleSeats} onChange={(e) => set('vehicleSeats', e.target.value)} /></Field>
      </div>
      <SaveBar busy={busy} error={error} saved={saved} />
    </form>
  );
}

export function RulesSettings() {
  const { profile } = useProfile();
  if (!profile) return <ProfileLoading />;
  return <RulesForm p={profile} />;
}

function RulesForm({ p }: { p: Profile }) {
  const { f, set } = useForm({
    minNoticeHours: p.minNoticeHours,
    maxAdvanceDays: p.maxAdvanceDays,
    bufferMin: p.bufferMin,
    autoConfirmDirect: p.autoConfirmDirect,
    policy: p.cancelPolicy as CancelPolicy,
    modes: new Set(p.acceptedModes),
    zelleHandle: p.zelleHandle ?? '',
  });
  const { run, busy, error, saved } = useSave();
  const toggle = (m: string) => {
    const next = new Set(f.modes);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    set('modes', next);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run({
      minNoticeHours: Number(f.minNoticeHours),
      maxAdvanceDays: Number(f.maxAdvanceDays),
      bufferMin: Number(f.bufferMin),
      autoConfirmDirect: f.autoConfirmDirect,
      cancelPolicy: { freeHours: Number(f.policy.freeHours), lateFeePct: Number(f.policy.lateFeePct), noShowPct: Number(f.policy.noShowPct) },
      acceptedModes: [...f.modes],
      zelleHandle: f.zelleHandle || null,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <Back title="Booking rules" />
      <Card title="Timing">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Min notice (hours)"><Input type="number" min={0} value={f.minNoticeHours} onChange={(e) => set('minNoticeHours', Number(e.target.value))} /></Field>
          <Field label="Book up to (days)"><Input type="number" min={1} value={f.maxAdvanceDays} onChange={(e) => set('maxAdvanceDays', Number(e.target.value))} /></Field>
          <Field label="Buffer (min)" hint="kept free after each ride"><Input type="number" min={0} step={15} value={f.bufferMin} onChange={(e) => set('bufferMin', Number(e.target.value))} /></Field>
        </div>
      </Card>
      <Card title="Cancellation policy" info="Shown to riders before they pay. If you cancel, the rider always gets everything back.">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Free until (hours before)"><Input type="number" min={0} value={f.policy.freeHours} onChange={(e) => set('policy', { ...f.policy, freeHours: Number(e.target.value) })} /></Field>
          <Field label="Late cancel keeps %"><Input type="number" min={0} max={100} value={f.policy.lateFeePct} onChange={(e) => set('policy', { ...f.policy, lateFeePct: Number(e.target.value) })} /></Field>
          <Field label="No-show keeps %"><Input type="number" min={0} max={100} value={f.policy.noShowPct} onChange={(e) => set('policy', { ...f.policy, noShowPct: Number(e.target.value) })} /></Field>
        </div>
      </Card>
      <Card title="How riders can pay">
        {[
          ['card', 'Card on the page (Apple Pay, Google Pay). $1 platform fee per card payment, plus Stripe processing.'],
          ['direct', 'Pay me directly (cash or as agreed). You confirm each request.'],
          ['zelle', 'Zelle'],
          ['cash', 'Cash'],
        ].map(([m, label]) => (
          <label key={m} className="flex items-start gap-3 py-2 text-sm">
            <input type="checkbox" className="mt-1" checked={f.modes.has(m!)} onChange={() => toggle(m!)} />
            <span>{label}</span>
          </label>
        ))}
        {f.modes.has('zelle') && <Field label="Zelle name or number"><Input value={f.zelleHandle} onChange={(e) => set('zelleHandle', e.target.value)} /></Field>}
        <label className="mt-3 flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-1" checked={f.autoConfirmDirect} onChange={(e) => set('autoConfirmDirect', e.target.checked)} />
          <span>Confirm "pay me directly" requests automatically (no tap needed; the time is blocked at once)</span>
        </label>
      </Card>
      <SaveBar busy={busy} error={error} saved={saved} />
    </form>
  );
}

export function PaymentsSettings() {
  const [params] = useSearchParams();
  const { profile, reload } = useProfile();
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    driverApi.stripeStatus().then(setStatus, () => setStatus(null));
    if (params.get('stripe')) void reload();
  }, [params, reload]);
  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await fn();
      location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Stripe did not answer. Try again.');
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <Back title="Card payments" />
      <p className="text-sm text-muted">Riders pay you by card through your page. You are the merchant: the money settles to your bank, your name shows on their statement, and you pay Stripe's processing fee plus a flat {BRAND.name} fee of $1 per card payment. Cash, Zelle and "pay me directly" bookings are free.</p>
      {error && <Notice tone="danger">{error}</Notice>}
      <Card title="Status" info="Stripe asks for your legal name, date of birth, SSN or EIN and a bank account. It takes about five minutes.">
        {status === null ? <p className="text-sm text-muted">Checking…</p> : !status.connected ? (
          <p className="text-sm">Not set up yet.</p>
        ) : (
          <div className="space-y-1 text-sm">
            <div>Card payments: <Badge value={status.chargesEnabled ? 'enabled' : 'pending'} tone={status.chargesEnabled ? 'ok' : 'warn'} /></div>
            <div>Payouts to your bank: <Badge value={status.payoutsEnabled ? 'enabled' : 'pending'} tone={status.payoutsEnabled ? 'ok' : 'warn'} /></div>
            {status.currentlyDue.length > 0 && <p className="text-xs text-muted">Stripe still needs: {status.currentlyDue.join(', ')}</p>}
            {status.disabledReason && <p className="text-xs text-danger">{status.disabledReason}</p>}
          </div>
        )}
      </Card>
      {!profile?.stripeChargesEnabled ? (
        <Button disabled={busy} className="w-full" onClick={() => void go(driverApi.stripeOnboarding)}>{status?.connected ? 'Continue Stripe setup' : 'Set up with Stripe'}</Button>
      ) : (
        <Button variant="outline" disabled={busy} className="w-full" onClick={() => void go(driverApi.stripeDashboard)}>Open Stripe dashboard (payouts, bank, tax forms)</Button>
      )}
    </div>
  );
}

export function BillingSettings() {
  const [params] = useSearchParams();
  const { reload } = useProfile();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    billingApi.status().then(setStatus, () => setStatus(null));
    if (params.get('checkout')) void reload();
  }, [params, reload]);
  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      location.href = (await fn()).url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open billing.');
      setBusy(false);
    }
  };
  const active = status?.planStatus === 'active' || status?.planStatus === 'trialing';
  const PLANS: Record<'standard' | 'pro', { price: string; blurb: string }> = {
    standard: { price: '$19 / month', blurb: 'Booking page, waybills, calendar, reminders, trip log.' },
    pro: { price: '$29 / month', blurb: 'Everything in Standard plus priority support and early access to new tools.' },
  };
  return (
    <div className="space-y-4">
      <Back title="Subscription" />
      {params.get('checkout') === 'ok' && <Notice tone="ok">Thanks! Your plan is being activated; this page updates in a moment.</Notice>}
      {error && <Notice tone="danger">{error}</Notice>}
      <Card title="Your plan">
        {status === null ? <p className="text-sm text-muted">Loading…</p> : (
          <div className="space-y-1 text-sm">
            <div>Status: <Badge value={status.planStatus} tone={active || status.planStatus === 'comped' ? 'ok' : status.planStatus === 'past_due' ? 'warn' : 'muted'} />{status.plan ? ` · ${status.plan}` : ''}</div>
            {status.currentPeriodEnd && <div className="text-muted">Renews {new Date(status.currentPeriodEnd).toLocaleDateString()}</div>}
            {status.compedUntil && <div className="text-muted">Complimentary until {new Date(status.compedUntil).toLocaleDateString()}</div>}
            {status.planStatus === 'past_due' && <div className="text-warn">Payment failed. Update your card within 7 days to keep your page live.</div>}
          </div>
        )}
      </Card>
      {status && !active && (
        <div className="grid gap-3 sm:grid-cols-2">
          {status.plans.map((p) => (
            <Card key={p.key} title={p.key === 'pro' ? 'Pro' : 'Standard'}>
              <div className="text-2xl font-black">{PLANS[p.key].price}</div>
              <p className="mt-1 text-sm text-ink2">{PLANS[p.key].blurb}</p>
              <p className="mt-1 text-xs text-muted">Plus $1 per card payment taken through your page. Cash, Zelle and direct bookings are free.</p>
              <Button className="mt-3 w-full" disabled={busy} onClick={() => void go(() => billingApi.checkout(p.key))}>Choose {p.key === 'pro' ? 'Pro' : 'Standard'}</Button>
            </Card>
          ))}
          {status.plans.length === 0 && <Notice tone="info">Online checkout is not switched on yet. Your onboarding contact activates your plan.</Notice>}
        </div>
      )}
      {status?.hasCustomer && <Button variant="outline" className="w-full" disabled={busy} onClick={() => void go(billingApi.portal)}>Manage billing (card, invoices, cancel)</Button>}
      <p className="text-xs text-muted">Cancel any time. Your records stay yours; the public page pauses when a plan ends.</p>
    </div>
  );
}

export function ShareSettings() {
  const { profile } = useProfile();
  const url = profile?.handle ? `${BRAND.publicOrigin}/${profile.handle}` : null;
  const short = url?.replace(/^https?:\/\//, '');
  // Full https link: every messaging app makes it tappable and it skips the http redirect.
  const template = `Thanks for riding with me! Book me directly next time, any card accepted: ${url}`;
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (what: string, text: string) => {
    await navigator.clipboard?.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };
  if (!url) return <div className="space-y-4"><Back title="Share your page" /><Notice tone="warn">Choose your page name first. <Link className="underline" to="/settings/profile">Business and page</Link></Notice></div>;
  return (
    <div className="space-y-4">
      <Back title="Share your page" />
      <Card title="Your link">
        <div className="break-all text-lg font-black">{short}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button small onClick={() => void copy('link', url)}>{copied === 'link' ? 'Copied' : 'Copy link'}</Button>
          {typeof navigator.share === 'function' && <Button small variant="ghost" onClick={() => void navigator.share({ url })}>Share…</Button>}
          <a className="rounded-2xl bg-card2 px-3 py-2 text-sm font-bold" href={url} target="_blank" rel="noreferrer">Open</a>
        </div>
      </Card>
      <Card title="Text to send after a ride">
        <p className="rounded-xl bg-card2 p-3 text-sm">{template}</p>
        <div className="mt-3 flex gap-2">
          <Button small onClick={() => void copy('tpl', template)}>{copied === 'tpl' ? 'Copied' : 'Copy text'}</Button>
          <a className="rounded-2xl bg-card2 px-3 py-2 text-sm font-bold" href={`sms:?&body=${encodeURIComponent(template)}`}>Open Messages</a>
        </div>
        <p className="mt-2 text-xs text-muted">Send it from your own phone, after the trip. Never solicit riders inside another app's trip.</p>
      </Card>
      <Card title="QR code for your card" info="Long-press to save. Print it on your business card or a dashboard placard.">
        <QrCode value={url} alt="QR code" className="mx-auto h-48 w-48 rounded-xl bg-white p-2" />
      </Card>
    </div>
  );
}

/** Deleting the account; trip records stay for the three years the law requires. */
function DeleteAccount({ onDeleted }: { onDeleted: () => void }) {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card title="Delete your account">
      <p className="text-sm text-ink2">
        Takes your page offline, cancels your plan and removes your sign-in, photos, services, hours, passenger list, messages and saved places. Your trip records (bookings and waybills) stay for the three years California law requires.
      </p>
      {error && <div className="mt-3"><Notice tone="danger">{error}</Notice></div>}
      <Button variant="danger" className="mt-3" onClick={() => setAsking(true)}>Delete my account</Button>
      {asking && (
        <Confirm
          title="Delete your account?"
          body="This cannot be undone. Finish or cancel upcoming rides first."
          confirmLabel="Delete"
          danger
          onCancel={() => setAsking(false)}
          onConfirm={async () => {
            setAsking(false);
            try {
              await driverApi.deleteAccount();
              onDeleted();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Could not delete the account.');
            }
          }}
        />
      )}
    </Card>
  );
}
